/**
 * The pictures the host gives for the two options. A screenshot off a big
 * screen is a few megabytes and only its shape matters, so it is redrawn small
 * before it goes up: the model reads the layout either way, and the upload is
 * quick enough to feel like nothing happened.
 */
import type { PageInspirations, PageSlot } from "../../shared/types";

/** The widest the picture is sent at. */
const MAX_WIDTH = 1600;
const QUALITY = 0.85;

export const SLOTS: readonly PageSlot[] = ["a", "b"];
export const SLOT_LETTER: Record<PageSlot, string> = { a: "A", b: "B" };

/** The first empty slot, A before B, or null when both are full. */
export function firstFree(value: PageInspirations): PageSlot | null {
  return SLOTS.find((slot) => !value[slot]) ?? null;
}

/** Redraw the picture at most `MAX_WIDTH` wide as a JPEG. */
export async function downscale(file: File): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_WIDTH / bitmap.width);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((done) => canvas.toBlob(done, "image/jpeg", QUALITY));
    if (!blob) return file;
    return new File([blob], `${file.name.replace(/\.[^.]+$/, "")}.jpg`, { type: "image/jpeg" });
  } catch {
    // A browser that cannot redraw it sends the picture as it is.
    return file;
  }
}
